import { useState, useEffect, useRef } from 'react';

interface Message {
  type: 'bot' | 'user';
  text: string;
  timestamp: Date;
}

export default function Footer() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      type: 'bot',
      text: 'Hi! 👋 I\'m Postey, your friendly MandaPost mascot! How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for custom event to open chatbot
  useEffect(() => {
    const handleOpenChat = () => {
      setIsChatOpen(true);
    };

    window.addEventListener('openChatbot', handleOpenChat);

    return () => {
      window.removeEventListener('openChatbot', handleOpenChat);
    };
  }, []);

  const getBotResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    // Pricing questions
    if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('pricing') || lowerMessage.includes('how much')) {
      return 'MandaPost offers flexible pricing starting at $29/month for individuals. We have Professional, Business, and Enterprise plans with volume discounts for agencies. All plans include a 14-day free trial with no credit card required! Visit /pricing to compare plans and see what fits your needs.';
    }

    // Free trial questions
    if (lowerMessage.includes('free trial') || lowerMessage.includes('trial') || lowerMessage.includes('try free')) {
      return 'Yes! We offer a generous 14-day free trial with full access to all features - no credit card required! You can cancel anytime during the trial with no charges. Plus, we have a 30-day money-back guarantee after you subscribe. Start your trial at /signup!';
    }

    // Getting started questions
    if (lowerMessage.includes('get started') || lowerMessage.includes('how do i start') || lowerMessage.includes('begin') || lowerMessage.includes('onboard')) {
      return 'Getting started is easy! 1) Sign up at /signup (takes 2 minutes), 2) Connect your social accounts, 3) Let our AI create your first content calendar, 4) Review and customize posts, 5) Schedule and publish! We also offer guided onboarding and free training webinars for all new users.';
    }

    // Features questions
    if (lowerMessage.includes('feature') || lowerMessage.includes('what can') || lowerMessage.includes('capabilities') || lowerMessage.includes('what does')) {
      return 'MandaPost offers: ✨ AI-powered content calendar & caption generation, 📅 Multi-platform scheduling (Instagram, TikTok, Twitter, Facebook, LinkedIn, YouTube, Pinterest), 📊 Advanced analytics & ROI tracking, 🎨 Visual content library, 👥 Team collaboration & client approvals, 🔍 Competitor analysis, 📈 Best time to post optimizer, and more! Explore all features at /features.';
    }

    // Calendar questions
    if (lowerMessage.includes('calendar') || lowerMessage.includes('schedule') || lowerMessage.includes('plan content')) {
      return 'Our visual marketing calendar is the heart of MandaPost! It uses Agentic AI to automatically plan your content for the entire month based on trends, holidays, and your audience. You can drag & drop to reschedule, bulk upload posts, and see all platforms in one view. The calendar updates in real-time and syncs across your team!';
    }

    // Caption/Content creation questions
    if (lowerMessage.includes('caption') || lowerMessage.includes('content creation') || lowerMessage.includes('write') || lowerMessage.includes('generate post')) {
      return 'Our AI Caption Generator creates engaging, on-brand captions in seconds! Choose your tone (professional, casual, funny, inspirational), select your platform, and get 5+ unique caption options. We also have a free Caption Generator tool at /tools/caption-generator you can try right now - no signup needed!';
    }

    // Hashtag questions
    if (lowerMessage.includes('hashtag') || lowerMessage.includes('#')) {
      return 'MandaPost includes an intelligent Hashtag Generator that finds trending and relevant hashtags for your niche! Get hashtag suggestions with reach estimates, track which tags perform best, and save hashtag sets for reuse. Try our free Hashtag Generator at /tools/hashtag-generator!';
    }

    // Tools questions
    if (lowerMessage.includes('free tools') || lowerMessage.includes('tools')) {
      return 'We offer amazing free tools at /tools including: AI Caption Generator, Hashtag Generator, and more coming soon like Best Time to Post Calculator, Bio Generator, and Engagement Rate Calculator. No signup required to use the free tools!';
    }

    // Analytics questions
    if (lowerMessage.includes('analytics') || lowerMessage.includes('reporting') || lowerMessage.includes('metrics') || lowerMessage.includes('track') || lowerMessage.includes('roi')) {
      return 'Our advanced analytics dashboard tracks: engagement rates, follower growth, best performing posts, optimal posting times, competitor comparisons, and complete ROI analysis. Export beautiful reports to PDF/CSV for clients. All data updates in real-time across all your connected platforms!';
    }

    // Platform/Integration questions
    if (lowerMessage.includes('platform') || lowerMessage.includes('integration') || lowerMessage.includes('connect') || lowerMessage.includes('social media')) {
      return 'MandaPost integrates with ALL major platforms: Instagram, Facebook, Twitter/X, LinkedIn, TikTok, YouTube, Pinterest, Google My Business, and more! We also integrate with Canva, Google Analytics, Zapier, and 100+ other marketing tools. Visit /integrations to see the full list!';
    }

    // AI questions
    if (lowerMessage.includes('ai') || lowerMessage.includes('artificial intelligence') || lowerMessage.includes('agentic') || lowerMessage.includes('intelligent') || lowerMessage.includes('automation')) {
      return 'MandaPost uses cutting-edge Agentic AI that acts like your personal marketing assistant! It automatically: generates content calendars, creates post captions, finds trending topics, optimizes posting times, suggests hashtags, analyzes competitor strategies, and learns from your audience engagement. It\'s like having an expert marketer working 24/7!';
    }

    // Best time to post questions
    if (lowerMessage.includes('best time') || lowerMessage.includes('when to post') || lowerMessage.includes('optimal time')) {
      return 'Our AI analyzes your audience\'s activity patterns and determines the optimal posting times for each platform to maximize engagement! The system continuously learns from your analytics and adjusts recommendations. This feature alone can increase engagement by 30-40%!';
    }

    // Support questions
    if (lowerMessage.includes('support') || lowerMessage.includes('customer service') || lowerMessage.includes('contact')) {
      return 'We offer world-class 24/7 support! 📧 Email: hello@mandapost.com (response within 24hrs), 💬 Live chat for Pro+ users, 📚 Comprehensive Help Center & video tutorials, 🎓 Free training webinars, and 📞 Phone support for Enterprise customers. Visit /contact to reach our team!';
    }

    // Who is it for questions
    if (lowerMessage.includes('who') || lowerMessage.includes('for me') || lowerMessage.includes('agency') || lowerMessage.includes('business') || lowerMessage.includes('freelancer')) {
      return 'MandaPost is perfect for: 🎯 Marketing agencies managing multiple clients, 🏢 Businesses of all sizes, 💼 Freelance social media managers, 📱 Content creators & influencers, 🛍️ E-commerce brands, and 👥 In-house marketing teams. Over 50,000+ marketers trust MandaPost!';
    }

    // Competitor comparison questions
    if (lowerMessage.includes('better than') || lowerMessage.includes('vs') || lowerMessage.includes('compare') || lowerMessage.includes('hootsuite') || lowerMessage.includes('buffer') || lowerMessage.includes('later')) {
      return 'Unlike other tools, MandaPost offers true Agentic AI that proactively creates and optimizes content (not just scheduling). We also provide: unlimited posts on all plans, advanced competitor tracking, visual calendar planning, built-in design tools, and 50% lower cost than Hootsuite/Sprout Social. See the full comparison at /compare!';
    }

    // Mobile app questions
    if (lowerMessage.includes('mobile') || lowerMessage.includes('app') || lowerMessage.includes('iphone') || lowerMessage.includes('android')) {
      return 'Yes! MandaPost has native iOS and Android apps with full functionality. Manage your content calendar, post on-the-go, get mobile notifications for engagement, and approve content from anywhere. The mobile app syncs instantly with the web platform!';
    }

    // Team questions
    if (lowerMessage.includes('team') || lowerMessage.includes('collaborate') || lowerMessage.includes('multiple users') || lowerMessage.includes('permissions')) {
      return 'MandaPost is built for teams! Features include: customizable user roles & permissions, client approval workflows, comment threads on posts, shared content library, task assignments, team activity logs, and unlimited team members on Business+ plans. Perfect for agencies managing multiple clients!';
    }

    // Content library questions
    if (lowerMessage.includes('content library') || lowerMessage.includes('media library') || lowerMessage.includes('images') || lowerMessage.includes('videos')) {
      return 'Our visual content library stores all your images, videos, and graphics in one organized place! Features: unlimited storage on Pro+ plans, smart tagging & search, integration with Canva & Unsplash, bulk upload, and shared team libraries. Never lose track of your brand assets again!';
    }

    // Security/Privacy questions
    if (lowerMessage.includes('secure') || lowerMessage.includes('security') || lowerMessage.includes('safe') || lowerMessage.includes('privacy') || lowerMessage.includes('gdpr') || lowerMessage.includes('data')) {
      return 'Your data security is our priority! We\'re: ✅ SOC 2 Type II Certified, ✅ GDPR & CCPA Compliant, ✅ 256-bit SSL Encrypted, ✅ Regular security audits, ✅ Two-factor authentication, and ✅ 99.9% uptime guarantee. We never sell your data. Learn more at /security.';
    }

    // Cancel/Refund questions
    if (lowerMessage.includes('cancel') || lowerMessage.includes('refund') || lowerMessage.includes('money back')) {
      return 'You can cancel your subscription anytime with just one click - no penalties, no hassles! We offer a 30-day money-back guarantee for new customers. Plus, you can export all your data before canceling. We want you to stay because you love the product, not because you\'re locked in!';
    }

    // Playground questions
    if (lowerMessage.includes('playground') || lowerMessage.includes('trending') || lowerMessage.includes('trend')) {
      return 'The Playground is our AI-powered trend discovery tool! See what\'s trending in your industry each day, get viral content ideas, discover emerging topics before your competitors, and find content gaps to fill. It\'s like having a crystal ball for social media trends! Check it out at /playground.';
    }

    // Demo questions
    if (lowerMessage.includes('demo') || lowerMessage.includes('schedule') || lowerMessage.includes('walkthrough')) {
      return 'I\'d love to show you MandaPost in action! You have two options: 1) Start your free 14-day trial at /signup and explore everything yourself (no credit card needed!), or 2) Schedule a personalized demo with our team at /contact where we\'ll walk you through features specific to your needs!';
    }

    // API/Developer questions
    if (lowerMessage.includes('api') || lowerMessage.includes('developer') || lowerMessage.includes('integrate')) {
      return 'Yes! We offer a comprehensive REST API for Enterprise customers. Integrate MandaPost into your own tools, build custom workflows, automate reporting, and more. Full API documentation and developer support available. Contact us at /contact for API access!';
    }

    // Training questions
    if (lowerMessage.includes('training') || lowerMessage.includes('learn') || lowerMessage.includes('tutorial') || lowerMessage.includes('how to use')) {
      return 'We offer extensive learning resources! 🎓 Free onboarding call for new users, 📺 Video tutorial library, 📖 Step-by-step guides, 🎯 Weekly webinars, 📚 Help Center with 200+ articles, and 💡 In-app tips & tooltips. You\'ll be a pro in no time!';
    }

    // Bulk/Scale questions
    if (lowerMessage.includes('bulk') || lowerMessage.includes('many posts') || lowerMessage.includes('scale') || lowerMessage.includes('lots of')) {
      return 'MandaPost handles bulk operations like a champ! Upload hundreds of posts via CSV, duplicate content across accounts, bulk reschedule campaigns, apply hashtag sets to multiple posts, and manage unlimited social profiles. Our agency clients schedule 1000+ posts per month with ease!';
    }

    // Greeting
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey') || lowerMessage.includes('sup')) {
      return 'Hello! 👋 I\'m Postey, MandaPost\'s friendly mascot and your personal assistant! I\'m here to answer any questions about features, pricing, getting started, or anything else. What would you like to know?';
    }

    // Thank you
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return 'You\'re very welcome! 😊 I\'m always here to help. Feel free to ask me anything else about MandaPost, or visit /signup to start your free trial!';
    }

    // Default response with suggestions
    return 'Great question! I can help you with:\n\n💰 Pricing & Plans\n🎯 Features & Capabilities\n🚀 Getting Started\n🤖 AI & Automation\n📊 Analytics & Reporting\n🔗 Platform Integrations\n👥 Team Collaboration\n🛠️ Free Tools\n🔒 Security & Privacy\n\nJust ask me anything, or visit /contact to speak directly with our team!';
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Get bot response
    setTimeout(() => {
      const botResponse: Message = {
        type: 'bot',
        text: getBotResponse(inputValue),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 500);

    setInputValue('');
  };

  const handleQuickAction = (question: string) => {
    // Add user message
    const userMessage: Message = {
      type: 'user',
      text: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Get bot response
    setTimeout(() => {
      const botResponse: Message = {
        type: 'bot',
        text: getBotResponse(question),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 500);
  };

  const footerSections = [
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy-policy' },
        { label: 'Terms of Use', href: '/terms-of-use' },
        { label: 'Terms of Service', href: '/terms-of-service' },
        { label: 'Subscription Terms', href: '/subscription-terms' },
        { label: 'Security', href: '/security' },
        { label: 'Cookie Policy', href: '/cookie-policy' }
      ]
    },
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'The Playground', href: '/playground' },
        { label: 'Integrations', href: '/integrations' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Affiliates & Referrals', href: '/affiliates' },
        { label: 'Careers', href: '/careers' },
        { label: 'Blog', href: '#blog' },
        { label: 'Press', href: '/press' }
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '#help' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Community', href: '#community' }
      ]
    },
    {
      title: 'FAQ',
      links: [
        { label: 'How does the free trial work?', href: '/faq#faq-trial' },
        { label: 'Which platforms are supported?', href: '/faq#faq-platforms' },
        { label: 'Can I cancel anytime?', href: '/faq#faq-cancel' },
        { label: 'Is my data secure?', href: '/faq#faq-security' },
        { label: 'How does AI content work?', href: '/faq#faq-ai' }
      ]
    }
  ];

  const socialLinks = [
    { icon: 'ri-twitter-x-line', href: 'https://twitter.com/MandaPostHQ', label: 'Twitter' },
    { icon: 'ri-linkedin-line', href: 'https://www.linkedin.com/company/mandapost', label: 'LinkedIn' },
    { icon: 'ri-facebook-line', href: 'https://facebook.com/MandaPostHQ', label: 'Facebook' },
    { icon: 'ri-instagram-line', href: 'https://instagram.com/MandaPostHQ', label: 'Instagram' },
    { icon: 'ri-youtube-line', href: 'https://youtube.com/@MandaPostHQ', label: 'YouTube' }
  ];

  return (
    <footer className="bg-[#22577A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Footer Links Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8 mb-12">
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="font-semibold mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-white/70 hover:text-white transition-colors cursor-pointer text-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-white">SOC 2 Type II</p>
                <p className="text-xs text-white/70">Certified</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-white">GDPR</p>
                <p className="text-xs text-white/70">Compliant</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <i className="ri-lock-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-white">256-bit SSL</p>
                <p className="text-xs text-white/70">Encrypted</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <i className="ri-pie-chart-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-white">99.9% Uptime</p>
                <p className="text-xs text-white/70">Guaranteed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section with Brand */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            {/* Logo and Description */}
            <div className="flex flex-col items-center lg:items-start max-w-md">
              <a href="/" className="inline-block mb-4">
                <img
                  src="/assets/MandaPost_White_Logo.png"
                  alt="MandaPost Logo"
                  className="h-10 w-auto transition-transform hover:scale-105"
                />
              </a>
              <p className="text-white/70 text-sm text-center lg:text-left mb-4">
                Revolutionizing social media marketing with AI-powered automation, intelligent content creation, and seamless team collaboration.
              </p>
              <a href="https://www.mandapost.com" className="text-[#b3d9ff] hover:text-[#66b3ff] transition-colors text-sm font-medium mb-3 inline-block">
                www.mandapost.com
              </a>
              <p className="text-white/70 text-sm">
                © 2026 MandaPost. All rights reserved.
              </p>
            </div>

            {/* Social Links */}
            <div className="flex flex-col items-center lg:items-end">
              <p className="text-white/80 font-semibold mb-4">Follow Us</p>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                    aria-label={social.label}
                  >
                    <i className={`${social.icon} text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Chat Bot */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        {/* Chat Window */}
        {isChatOpen && (
          <div className="mb-4 bg-white rounded-2xl shadow-2xl border-2 border-[#66b3ff] w-[calc(100vw-2rem)] max-w-xs sm:max-w-sm md:w-96 animate-fadeIn">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white p-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="/assets/Postey_Mascot.jpg"
                  alt="Postey"
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                />
                <div>
                  <h3 className="font-bold">Chat with Postey</h3>
                  <p className="text-xs text-white/80">Your friendly MandaPost assistant</p>
                </div>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="hover:bg-white/20 rounded-lg p-1 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="p-3 sm:p-4 h-64 sm:h-80 overflow-y-auto bg-gray-50">
              <div className="space-y-3">
                {messages.map((message, index) => (
                  <div key={index}>
                    {message.type === 'bot' ? (
                      <div className="flex gap-2">
                        <img
                          src="/assets/Postey_Mascot.jpg"
                          alt="Postey"
                          className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                        />
                        <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-sm max-w-[70%]">
                          <p className="text-sm text-gray-800">{message.text}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <div className="bg-gray-200 rounded-lg rounded-tr-none p-3 shadow-sm max-w-[70%]">
                          <p className="text-sm text-gray-800">{message.text}</p>
                        </div>
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-gray-600 text-sm"></i>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Quick Actions - only show if first message */}
                {messages.length === 1 && (
                  <div className="space-y-2 ml-10">
                    <button
                      onClick={() => handleQuickAction('How does the free trial work?')}
                      className="w-full text-left bg-white hover:bg-[#e4f4ff]/30 border border-gray-200 hover:border-[#66b3ff] rounded-lg p-3 text-sm transition-all text-gray-800"
                    >
                      <i className="ri-rocket-line text-[#22577A] mr-2"></i>
                      How does the free trial work?
                    </button>
                    <button
                      onClick={() => handleQuickAction('What features do you have?')}
                      className="w-full text-left bg-white hover:bg-[#e4f4ff]/30 border border-gray-200 hover:border-[#66b3ff] rounded-lg p-3 text-sm transition-all text-gray-800"
                    >
                      <i className="ri-sparkling-line text-[#22577A] mr-2"></i>
                      What features do you have?
                    </button>
                    <button
                      onClick={() => handleQuickAction('How does the AI work?')}
                      className="w-full text-left bg-white hover:bg-[#e4f4ff]/30 border border-gray-200 hover:border-[#66b3ff] rounded-lg p-3 text-sm transition-all text-gray-800"
                    >
                      <i className="ri-robot-line text-[#22577A] mr-2"></i>
                      How does the AI work?
                    </button>
                    <button
                      onClick={() => handleQuickAction('What are your pricing plans?')}
                      className="w-full text-left bg-white hover:bg-[#e4f4ff]/30 border border-gray-200 hover:border-[#66b3ff] rounded-lg p-3 text-sm transition-all text-gray-800"
                    >
                      <i className="ri-price-tag-3-line text-[#22577A] mr-2"></i>
                      What are your pricing plans?
                    </button>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  autoFocus
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#66b3ff] text-sm bg-white"
                  style={{ pointerEvents: 'auto' }}
                />
                <button
                  onClick={handleSendMessage}
                  type="button"
                  className="px-4 py-2 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <i className="ri-send-plane-fill"></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Powered by MandaPost AI
              </p>
            </div>
          </div>
        )}

        {/* Chat Button */}
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] text-white rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all flex items-center justify-center group overflow-hidden border-2 border-white/20"
        >
          {isChatOpen ? (
            <i className="ri-close-line text-2xl"></i>
          ) : (
            <img
              src="/assets/Postey_Mascot.jpg"
              alt="Chat with Postey"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform"
            />
          )}
        </button>
      </div>
    </footer>
  );
}
